// @flow

import _ from 'lodash';
import type { InstallState, InstallAction } from 'stores/flow-type/install.type';

import { processIntallStatus } from 'src/helpers/installation-helper';
import {
  INSTALL_APP_REQUEST,
  INSTALL_APP_SUCCESS,
  INSTALL_APP_FAILURE,
  ADD_DEMOGRAPHIC_REQUEST,
  ADD_DEMOGRAPHIC_SUCCESS,
  ADD_DEMOGRAPHIC_FAILURE,
  GET_INSTALL_STATUS_REQUEST,
  GET_INSTALL_STATUS_SUCCESS,
  GET_INSTALL_STATUS_FAILURE,
  UPDATE_INSTALLATION_STATUS_REQUEST,
  UPDATE_INSTALLATION_STATUS_FAILURE,
  UPDATE_INSTALLATION_KYC_STATUS_REQUEST,
  UPDATE_INSTALLATION_KYC_STATUS_SUCCESS,
  UPDATE_INSTALLATION_KYC_STATUS_FAILURE,
  CHECKED_SHOW_ONBOARDING_PLAN,
  STORE_STATE_SETUP_THEME
} from 'stores/flow-type/install.type';
import { TURBO_FEATURE } from 'src/constants/installation-v2';

const initialState: InstallState = {
  loading: false,
  success: false,
  error: undefined,
  installationError: {},
  code: '',
  shop: '',
  message: '',
  syncStatus: undefined,
  syncProgress: 0,
  syncStartedAt: 0,
  syncRemainingTime: 0,
  installStatus: undefined,
  installSyncPercent: 0,
  installSetupThemePercent: 0,
  installStep: 0,
  installTotal: 0,
  installCurrentStep: undefined,
  installThemeId: undefined,
  installThemeName: '',
  installThemeUpdatedAt: 0,
  installFeatures: {},
  installShopifyThemeName: '',
  installThemeLibVersion: '',
  syncInfoLastSyncedStatus: '',
  shownOnboardingPlan: false,
  syncInfoLastSyncedDoneAt: 0,
  currentSelectedFeatures: {
    [TURBO_FEATURE.COLLECTION_FILTER]: false,
    [TURBO_FEATURE.SEARCH_PAGE_FILTER]: false,
    [TURBO_FEATURE.ISW]: false,
    [TURBO_FEATURE.OTHERS]: false
  },
  currentSelectedThemeId: undefined
};

const installReducers = (state: InstallState = initialState, action: InstallAction) => {
  switch (action.type) {
    case INSTALL_APP_REQUEST:
    case GET_INSTALL_STATUS_REQUEST:
    case UPDATE_INSTALLATION_STATUS_REQUEST:
    case UPDATE_INSTALLATION_KYC_STATUS_REQUEST:
    case ADD_DEMOGRAPHIC_REQUEST:
      return {
        ...state,
        loading: true,
        success: false,
        error: undefined,
        installationError: {}
      };
    case INSTALL_APP_SUCCESS: {
      const { data } = action.payload;
      const { code, shop, message } = data;
      return {
        ...state,
        loading: false,
        success: true,
        installationError: {},
        code,
        shop,
        message
      };
    }
    case ADD_DEMOGRAPHIC_SUCCESS: {
      return { ...state, loading: false, success: true };
    }
    case CHECKED_SHOW_ONBOARDING_PLAN: {
      return {
        ...state,
        shownOnboardingPlan: true
      };
    }
    case GET_INSTALL_STATUS_SUCCESS: {
      const { data } = action.payload;
      const syncStatus = _.get(data, 'syncStatus.status', 'done');
      const syncProgress = _.get(data, 'syncStatus.progress');
      const syncStartedAt = _.get(data, 'syncStatus.startedAt');
      const syncRemainingTime = _.get(data, 'syncStatus.remainingTime');
      const installStatus = _.get(data, 'autoThemeSetupStatus.status');
      const installThemeId = _.get(data, 'autoThemeSetupStatus.themeId');
      const installThemeName = _.get(data, 'autoThemeSetupStatus.themeName');
      const installThemeUpdatedAt = _.get(data, 'autoThemeSetupStatus.updatedAt');
      const installFeatures = _.get(data, 'autoThemeSetupStatus.taeFeatures', {});
      const installShopifyThemeName = _.get(data, 'autoThemeSetupStatus.shopifyThemeName', '');
      const installThemeLibVersion = _.get(data, 'autoThemeSetupStatus.boostThemeLibVersion', '');
      const { currentStep, step, total, syncPercent, setupThemePercent } = processIntallStatus(
        data,
        state.installSyncPercent,
        state.installSetupThemePercent
      );

      let themeSetupInfoIsLiveTheme = _.get(data, 'autoThemeSetupStatus.isLiveTheme', false);
      if (typeof themeSetupInfoIsLiveTheme === 'string' && themeSetupInfoIsLiveTheme === 'false') {
        themeSetupInfoIsLiveTheme = false;
      }
      if (typeof themeSetupInfoIsLiveTheme === 'string' && themeSetupInfoIsLiveTheme === 'true') {
        themeSetupInfoIsLiveTheme = true;
      }

      const syncInfoLastSyncedStatus = _.get(data, 'syncStatus.lastSynced.status');
      const syncInfoLastSyncedDoneAt = _.get(data, 'syncStatus.lastSynced.doneAt');
      const themeSetupInfoBoostThemeLib = _.get(data, 'autoThemeSetupStatus.boostThemeLib');

      return {
        ...state,
        loading: false,
        error: undefined,
        syncStatus,
        syncProgress,
        syncStartedAt,
        syncRemainingTime,
        installThemeId,
        installThemeName,
        installThemeUpdatedAt,
        installStatus,
        installSyncPercent: syncPercent,
        installSetupThemePercent: setupThemePercent,
        installCurrentStep: currentStep,
        installStep: step,
        installTotal: total,
        installFeatures,
        installShopifyThemeName,
        installThemeLibVersion,

        syncInfoLastSyncedStatus,
        syncInfoLastSyncedDoneAt,
        themeSetupInfoIsLiveTheme,
        themeSetupInfoBoostThemeLib
      };
    }
    case INSTALL_APP_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        installationError: action.payload
      };
    case ADD_DEMOGRAPHIC_FAILURE:
    case GET_INSTALL_STATUS_FAILURE:
    case UPDATE_INSTALLATION_STATUS_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };

    case UPDATE_INSTALLATION_KYC_STATUS_SUCCESS: {
      const features = _.get(action, 'payload.data.features', []);
      const industries = _.get(action, 'payload.data.industries', []);
      const revenue = _.get(action, 'payload.data.revenue', null);
      return {
        ...state,
        loading: false,
        success: true,
        error: undefined,
        KYCValue: {
          features,
          industries,
          revenue
        }
      };
    }
    case UPDATE_INSTALLATION_KYC_STATUS_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };
    case STORE_STATE_SETUP_THEME: {
      const { selectedFeatures, selectedThemeId } = action.payload;
      return {
        ...state,
        currentSelectedFeatures: selectedFeatures,
        currentSelectedThemeId: selectedThemeId
      };
    }
    default:
      return state;
  }
};
export default installReducers;
